import {
    Button,
    Callout,
    Dialog,
    DialogBody,
    DialogFooter,
    Menu,
    MenuItem,
    Spinner,
    Tag
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useGetPoiBusesByNiraBusIdQuery } from "generated/graphql";
import React, { useEffect, useMemo, useState } from "react";
import { RegionType } from "types/regionType";
import { Scope } from "types/scope";
import { ScopeViewType } from "types/scopeViewType";
import {
    acceptsChargingMw,
    buildPoiEligibilityFilters,
    canCopySpeculativeProjects,
    formatPriorSpeculativeStudySubtitle,
    formatSpeculativePoiBus,
    formatStudyBusUtilities,
    getSpeculativeFuelTypeLabel,
    makeSpeculativeProjectDraftsFromPriorProjects,
    MAX_SPECULATIVE_PROJECTS,
    normalizeSpeculativeFuelType,
    PriorSpeculativeProject,
    PriorSpeculativeStudy,
    SpeculativePoiBus,
    SpeculativeProjectDraft,
    StudyBusUtilities,
    toSpeculativePoiBusesByNiraBusId
} from "utils/speculativeProjectUtils";
import "./CopyFromPriorStudyDialog.scss";

/**
 * "Copy from a previous study" for the New projects wizard step: pick one of
 * the team's earlier local studies in this region and bring its speculative
 * projects across as rows of the study being created.
 *
 * Copied rows are appended to whatever the user has already added, so a copy
 * that would overrun the `spec_N` namespace is blocked rather than truncated.
 *
 * A POI only survives the copy if the study being created can use it: the bus
 * is in the *new* base scope's models, and in a utility the new study bus
 * resolves to. Base scopes differ in bus coverage — WECC's all-generators scope
 * carries ~1,400 buses its base scope does not — and the source study may have
 * sat in another utility entirely, so both are checked here rather than
 * assumed. A project whose POI does not resolve is still copied, with the POI
 * blank and the dropped bus named on the row.
 */

/** Resolves the selected study's POIs against the study being created. */
const usePoiBusesForStudy = (
    study: PriorSpeculativeStudy | undefined,
    region: RegionType,
    studyBus: StudyBusUtilities,
    baseScope: Scope,
    scopeViews: ReadonlyArray<ScopeViewType>,
    skip: boolean
): {
    readonly poiBusesByNiraBusId: ReadonlyMap<string, SpeculativePoiBus>;
    readonly loading: boolean;
    readonly failed: boolean;
} => {
    const niraBusIds = useMemo(
        () =>
            Array.from(
                new Set(
                    (study?.projects ?? []).map((project) => project.niraBusId)
                )
            ),
        [study]
    );

    // Keyed on the utilities rather than on the bus: the bus page rebuilds its
    // bus object on every render, which is exactly what this memo is here to
    // keep out of the filter array.
    const { utilityDischarging, utilityStorage } = studyBus;
    // Spread here rather than at the Apollo boundary below: the query
    // variables want a mutable array, and materializing it inside the memo
    // keeps the identity stable across renders, which is the whole point.
    const poiFilters = useMemo(
        () => [
            ...buildPoiEligibilityFilters(baseScope.scope, scopeViews, {
                utilityDischarging,
                utilityStorage
            })
        ],
        [baseScope.scope, scopeViews, utilityDischarging, utilityStorage]
    );

    const { data, loading, error } = useGetPoiBusesByNiraBusIdQuery({
        variables: {
            region,
            nira_bus_ids: niraBusIds,
            scope_view_filters: poiFilters
        },
        // With no selected views the filter list is an empty `_and`, which
        // Hasura reads as "no restriction" — that would resolve every POI
        // against no base scope at all. No view means no bus qualifies.
        skip: skip || niraBusIds.length === 0 || scopeViews.length === 0
    });

    const poiBusesByNiraBusId = useMemo(
        () => toSpeculativePoiBusesByNiraBusId(data?.buses ?? []),
        [data]
    );

    return { poiBusesByNiraBusId, loading, failed: error !== undefined };
};

const CopyProjectRow: React.FC<{
    readonly project: PriorSpeculativeProject;
    readonly poiBus: SpeculativePoiBus | undefined;
    readonly poiLookupFailed: boolean;
    readonly checked: boolean;
    readonly onToggle: () => void;
}> = ({ project, poiBus, poiLookupFailed, checked, onToggle }) => {
    // The fuel type the copied row will hold, not the one stored: a value the
    // form no longer offers lands on "Other", so the stored one would preview a
    // row that isn't what the copy produces. The label is what the row's select
    // shows, the way every other piece of copy that names a fuel type reads.
    const fuelType = normalizeSpeculativeFuelType(project.fuelType);
    return (
        <label className="bp5-control bp5-checkbox CopyFromPriorStudyDialog-project">
            <input type="checkbox" checked={checked} onChange={onToggle} />
            <span className="bp5-control-indicator" />
            <strong>{project.name}</strong>
            <div className="bp5-text-muted bp5-text-small">
                {getSpeculativeFuelTypeLabel(fuelType)} · {project.nameplateMw}{" "}
                MW
                {acceptsChargingMw(fuelType) && project.chargingMw !== undefined
                    ? ` · ${project.chargingMw} MW charging`
                    : ""}{" "}
                ·{" "}
                {poiBus === undefined
                    ? `bus ${project.busId}`
                    : formatSpeculativePoiBus(poiBus)}
            </div>
            {!poiLookupFailed && poiBus === undefined && (
                <Tag
                    minimal
                    intent="warning"
                    className="CopyFromPriorStudyDialog-project-warning"
                >
                    POI not usable in this study — copied without one
                </Tag>
            )}
        </label>
    );
};

const CopyFromPriorStudyDialog: React.FC<{
    readonly isOpen: boolean;
    /** Newest first; never empty (the caller hides the entry point otherwise). */
    readonly studies: ReadonlyArray<PriorSpeculativeStudy>;
    readonly region: RegionType;
    /** The bus being studied, whose utility a copied POI has to share. */
    readonly studyBus: StudyBusUtilities;
    readonly baseScope: Scope;
    readonly scopeViews: ReadonlyArray<ScopeViewType>;
    /** How many rows the step already holds; copied rows are appended to them. */
    readonly existingDraftCount: number;
    readonly onClose: () => void;
    readonly onCopy: (drafts: ReadonlyArray<SpeculativeProjectDraft>) => void;
}> = ({
    isOpen,
    studies,
    region,
    studyBus,
    baseScope,
    scopeViews,
    existingDraftCount,
    onClose,
    onCopy
}) => {
    // Tracking the deselected projects rather than the selected ones is what
    // makes "everything checked" the state a freshly picked study starts in.
    const [selectedScope, setSelectedScope] = useState<string | undefined>(
        undefined
    );
    const [deselectedProjectNumbers, setDeselectedProjectNumbers] = useState<
        ReadonlySet<string>
    >(() => new Set());

    // The parent's study query revalidates on mount, so `studies` can change
    // while this dialog is open. Without this pin the source study would
    // follow the new studies[0], carrying deselections made against the study
    // the user actually picked onto whatever replaced it.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (studies.some((study) => study.scope === selectedScope)) {
            return;
        }
        setSelectedScope(studies[0]?.scope);
        setDeselectedProjectNumbers(new Set());
    }, [isOpen, selectedScope, studies]);

    const selectedStudy =
        studies.find((study) => study.scope === selectedScope) ?? studies[0];

    const { poiBusesByNiraBusId, loading, failed } = usePoiBusesForStudy(
        selectedStudy,
        region,
        studyBus,
        baseScope,
        scopeViews,
        !isOpen
    );

    const studyProjects = selectedStudy?.projects ?? [];
    const selectedProjects = studyProjects.filter(
        (project) => !deselectedProjectNumbers.has(project.projectNumber)
    );

    const selectStudy = (scope: string) => {
        setSelectedScope(scope);
        setDeselectedProjectNumbers(new Set());
    };

    const toggleProject = (projectNumber: string) => {
        const next = new Set(deselectedProjectNumbers);
        if (next.has(projectNumber)) {
            next.delete(projectNumber);
        } else {
            next.add(projectNumber);
        }
        setDeselectedProjectNumbers(next);
    };

    const handleClose = () => {
        setSelectedScope(undefined);
        setDeselectedProjectNumbers(new Set());
        onClose();
    };

    const handleCopy = () => {
        onCopy(
            makeSpeculativeProjectDraftsFromPriorProjects(
                selectedProjects,
                poiBusesByNiraBusId
            )
        );
        handleClose();
    };

    const overCap = !canCopySpeculativeProjects(
        existingDraftCount,
        selectedProjects.length
    );
    const unresolvedCount = selectedProjects.filter(
        (project) => !poiBusesByNiraBusId.has(project.niraBusId)
    ).length;
    // Named in the callout below: without it, a project dropped for sitting
    // outside the study's utility reads as a base-scope problem the user
    // cannot see.
    const studyUtilities = formatStudyBusUtilities(studyBus);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title="Copy new projects from a previous study"
            icon={IconNames.DUPLICATE}
            className="CopyFromPriorStudyDialog"
        >
            <DialogBody>
                <div className="CopyFromPriorStudyDialog-split">
                    <Menu className="CopyFromPriorStudyDialog-studies">
                        {studies.map((study) => (
                            <MenuItem
                                key={study.scope}
                                active={study.scope === selectedStudy?.scope}
                                onClick={() => selectStudy(study.scope)}
                                text={
                                    <>
                                        <strong>{study.displayName}</strong>
                                        <div className="bp5-text-small CopyFromPriorStudyDialog-study-subtitle">
                                            {formatPriorSpeculativeStudySubtitle(
                                                study
                                            )}
                                        </div>
                                    </>
                                }
                            />
                        ))}
                    </Menu>
                    <div className="CopyFromPriorStudyDialog-projects">
                        {loading ? (
                            <Spinner />
                        ) : (
                            <>
                                <div className="bp5-text-muted CopyFromPriorStudyDialog-count">
                                    {selectedProjects.length} of{" "}
                                    {studyProjects.length} projects selected
                                </div>
                                {studyProjects.map((project) => (
                                    <CopyProjectRow
                                        key={project.projectNumber}
                                        project={project}
                                        poiBus={poiBusesByNiraBusId.get(
                                            project.niraBusId
                                        )}
                                        poiLookupFailed={failed}
                                        checked={
                                            !deselectedProjectNumbers.has(
                                                project.projectNumber
                                            )
                                        }
                                        onToggle={() =>
                                            toggleProject(project.projectNumber)
                                        }
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
                {overCap && selectedProjects.length > 0 && (
                    <Callout
                        intent="danger"
                        icon={IconNames.WARNING_SIGN}
                        className="CopyFromPriorStudyDialog-callout"
                    >
                        A study can hold {MAX_SPECULATIVE_PROJECTS} new
                        projects. You have {existingDraftCount} and picked{" "}
                        {selectedProjects.length} — deselect{" "}
                        {existingDraftCount +
                            selectedProjects.length -
                            MAX_SPECULATIVE_PROJECTS}{" "}
                        to copy the rest.
                    </Callout>
                )}
                {failed && (
                    <Callout
                        intent="danger"
                        icon={IconNames.WARNING_SIGN}
                        className="CopyFromPriorStudyDialog-callout"
                    >
                        The POI lookup for this study failed, so its projects
                        cannot be copied right now. Close the dialog and try
                        again.
                    </Callout>
                )}
                {!loading && !failed && !overCap && unresolvedCount > 0 && (
                    <Callout
                        intent="warning"
                        icon={IconNames.INFO_SIGN}
                        className="CopyFromPriorStudyDialog-callout"
                    >
                        {unresolvedCount} of the projects you picked sit at a
                        bus this study can&apos;t use: a POI has to be in{" "}
                        {baseScope.display_name}
                        {studyUtilities !== undefined &&
                            `, and operated by ${studyUtilities}`}
                        . They will be copied without a POI for you to re-point.
                    </Callout>
                )}
            </DialogBody>
            <DialogFooter
                actions={
                    <>
                        <Button text="Cancel" onClick={handleClose} />
                        <Button
                            intent="primary"
                            text={`Copy ${selectedProjects.length} ${
                                selectedProjects.length === 1
                                    ? "project"
                                    : "projects"
                            }`}
                            disabled={loading || overCap || failed}
                            onClick={handleCopy}
                        />
                    </>
                }
            />
        </Dialog>
    );
};

export default CopyFromPriorStudyDialog;
